import { z } from 'zod'
import {
  TRAINER_CONTRACT_VERSION,
  trainerWriteSideEffectsSchema,
} from './trainer-event-read'
import { trainerCourseTypeSummarySchema } from './trainer-catalog-read'

const legacyMutationValue = z.unknown().optional()

/**
 * Course-type handlers retain the legacy validation boundary and aliases. These
 * request schemas describe the transport accepted by the route without moving
 * its exact 400 messages into the shared contract.
 */
export const trainerCourseTypeCreateRequestSchema = z.object({
  name: legacyMutationValue,
  label: legacyMutationValue,
  displayOrder: legacyMutationValue,
  display_order: legacyMutationValue,
  compatibilityAliases: legacyMutationValue,
  compatibility_aliases: legacyMutationValue,
}).passthrough()

export const trainerCourseTypeUpdateRequestSchema = trainerCourseTypeCreateRequestSchema
export const trainerCourseTypeLifecycleRequestSchema = z.object({}).passthrough()

const courseTypeUsageSchema = z.object({
  events: z.number().int().nonnegative(),
  resources: z.number().int().nonnegative(),
  invoices: z.number().int().nonnegative(),
  certificateGenerations: z.number().int().nonnegative(),
  total: z.number().int().nonnegative(),
  destructiveChangeAllowed: z.literal(false),
  destructiveChangeReason: z.literal('hard_delete_unavailable_preserve_historical_records'),
}).passthrough()

const courseTypeMutationEvidenceSchema = z.object({
  sourceOfTruth: z.literal('DB1'),
  tablesRead: z.tuple([
    z.literal('trainer_course_types'),
    z.literal('trainer_events'),
    z.literal('trainer_resource_files'),
    z.literal('trainer_invoices'),
    z.literal('trainer_certificate_form_generations'),
  ]),
  tablesWritten: z.tuple([z.literal('trainer_course_types'), z.literal('domain_audit_log')]),
  projection: z.literal('trainer-course-type-admin-mutation'),
  rawPayloadReturned: z.literal(false),
}).passthrough()

export const trainerCourseTypeMutationResponseSchema = z.object({
  contract_version: z.literal(TRAINER_CONTRACT_VERSION),
  courseType: trainerCourseTypeSummarySchema,
  usage: courseTypeUsageSchema,
  idempotentReplay: z.boolean(),
  catalogEvidence: courseTypeMutationEvidenceSchema,
  sideEffects: trainerWriteSideEffectsSchema.extend({
    deployLiveTraffic: z.literal(false),
    destructiveCleanup: z.literal(false),
  }).passthrough(),
}).passthrough()

export const trainerCourseTypeCreateResponseSchema = trainerCourseTypeMutationResponseSchema
export const trainerCourseTypeUpdateResponseSchema = trainerCourseTypeMutationResponseSchema
export const trainerCourseTypeArchiveResponseSchema = trainerCourseTypeMutationResponseSchema
export const trainerCourseTypeRestoreResponseSchema = trainerCourseTypeMutationResponseSchema
export const trainerCourseTypeDeprecateResponseSchema = trainerCourseTypeMutationResponseSchema

export type TrainerCourseTypeCreateRequest = z.infer<typeof trainerCourseTypeCreateRequestSchema>
export type TrainerCourseTypeUpdateRequest = z.infer<typeof trainerCourseTypeUpdateRequestSchema>
export type TrainerCourseTypeLifecycleRequest = z.infer<typeof trainerCourseTypeLifecycleRequestSchema>
export type TrainerCourseTypeMutationResponse = z.infer<typeof trainerCourseTypeMutationResponseSchema>
export type TrainerCourseTypeCreateResponse = z.infer<typeof trainerCourseTypeCreateResponseSchema>
export type TrainerCourseTypeUpdateResponse = z.infer<typeof trainerCourseTypeUpdateResponseSchema>
export type TrainerCourseTypeArchiveResponse = z.infer<typeof trainerCourseTypeArchiveResponseSchema>
export type TrainerCourseTypeRestoreResponse = z.infer<typeof trainerCourseTypeRestoreResponseSchema>
export type TrainerCourseTypeDeprecateResponse = z.infer<typeof trainerCourseTypeDeprecateResponseSchema>
